import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { toast } from 'sonner';
import { 
  Search, Plus, MapPin, DollarSign, Tag, MessageCircle, 
  Clock, User, X, Camera, Send, Users, Flag, AlertTriangle,
  Image, Loader2, ChevronLeft, ChevronRight, Lock, ArrowRight, ArrowLeft, Check
} from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import api from '@/lib/api';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const CATEGORIES = [
  { value: 'all', label: 'All Categories' },
  { value: 'furniture', label: 'Furniture' },
  { value: 'baby_gear', label: 'Baby Gear' },
  { value: 'nanny_childcare', label: 'Nanny & Childcare' },
  { value: 'house_cleaning', label: 'House Cleaning' },
  { value: 'tutoring', label: 'Tutoring' },
  { value: 'meal_services', label: 'Meal Services' },
  { value: 'clothing', label: 'Clothing' },
  { value: 'toys', label: 'Toys' },
  { value: 'wellness', label: 'Wellness' },
  { value: 'fitness', label: 'Fitness' },
  { value: 'other', label: 'Other' }
];

const RADIUS_OPTIONS = [
  { value: '5', label: '5 miles' },
  { value: '10', label: '10 miles' },
  { value: '25', label: '25 miles' },
  { value: '50', label: '50 miles' }
];


const REPORT_REASONS = [
  { value: 'spam', label: 'Spam or scam' },
  { value: 'inappropriate', label: 'Inappropriate content' },
  { value: 'hate_speech', label: 'Hate speech or discrimination' },
  { value: 'scam', label: 'Fraudulent listing' },
  { value: 'other', label: 'Other' }
];

function getImageUrl(imageId) {
  if (!imageId) return null;
  if (imageId.startsWith('http') || imageId.startsWith('data:')) return imageId;
  return `${API_URL}/api/upload/image/${imageId}`;
}

function ListingPhotoCarousel({ images }) {
  const [idx, setIdx] = useState(0);
  if (!images || images.length === 0) {
    return (
      <div className="h-40 bg-seafoam/20 flex items-center justify-center">
        <Camera className="text-text-light" size={32} />
      </div>
    );
  }
  return (
    <div className="h-40 relative group bg-gray-100">
      <img
        src={getImageUrl(images[idx])}
        alt="Listing"
        className="w-full h-full object-cover"
        loading="lazy"
      />
      {images.length > 1 && (
        <>
          <button
            onClick={(e) => { e.stopPropagation(); setIdx(i => (i - 1 + images.length) % images.length); }}
            className="absolute left-1 top-1/2 -translate-y-1/2 bg-black/40 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity"
          >
            <ChevronLeft size={16} />
          </button>
          <button
            onClick={(e) => { e.stopPropagation(); setIdx(i => (i + 1) % images.length); }}
            className="absolute right-1 top-1/2 -translate-y-1/2 bg-black/40 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity"
          >
            <ChevronRight size={16} />
          </button>
          <div className="absolute bottom-2 left-1/2 -translate-x-1/2 flex gap-1">
            {images.map((_, i) => (
              <span key={i} className={`w-1.5 h-1.5 rounded-full ${i === idx ? 'bg-white' : 'bg-white/50'}`} />
            ))}
          </div>
        </>
      )}
      <Badge className="absolute top-2 right-2 bg-black/50 text-white text-xs border-0">
        <Image size={10} className="mr-1" />{images.length}
      </Badge>
    </div>
  );
}

const PAID_TIERS = ['thrive', 'premium', 'connected'];
const hasMarketplaceAccess = (user) => user?.is_admin || PAID_TIERS.includes(user?.membership_tier);

function UpgradeModal({ open, onClose }) {
  const navigate = useNavigate();
  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-md" data-testid="upgrade-modal">
        <DialogHeader>
          <DialogTitle className="font-display text-navy text-center text-xl">
            Included with Thrive — plus one workshop every month
          </DialogTitle>
          <DialogDescription className="text-center text-text-mid">
            Thrive is $39/month — less than a single workshop — and includes one every calendar month, 30% off any additional workshops, the full Community Marketplace, priority matching, and all member gatherings.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4 mt-2">
          <div className="border border-teal bg-teal/5 rounded-2xl p-4">
            <div className="flex items-center justify-between mb-2">
              <h4 className="font-semibold text-navy">Thrive</h4>
              <span className="font-bold text-navy">$39<span className="text-xs text-text-light font-normal">/mo</span></span>
            </div>
            <ul className="space-y-1.5 mb-3">
              {[
                '1 workshop free every month (resets on the 1st)',
                '30% off any additional workshops',
                '24-hour early workshop registration',
                'Full Community Marketplace + all member gatherings',
              ].map(f => (
                <li key={f} className="text-sm text-text-mid flex items-center gap-2">
                  <Check size={14} className="text-teal flex-shrink-0" /> {f}
                </li>
              ))}
            </ul>
            <Button
              onClick={() => navigate('/pricing')}
              className="w-full bg-teal hover:bg-teal-deep text-white rounded-full"
              data-testid="upgrade-thrive-btn"
            >
              Upgrade to Thrive <ArrowRight size={16} className="ml-1" />
            </Button>
          </div>
        </div>
        <button
          onClick={onClose}
          className="text-xs text-text-light hover:text-text-mid text-center w-full mt-1"
          data-testid="stay-free-btn"
        >
          Stay on free plan
        </button>
      </DialogContent>
    </Dialog>
  );
}

export default function CommunityBoardPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const fullAccess = hasMarketplaceAccess(user);
  const [upgradeModalOpen, setUpgradeModalOpen] = useState(false);
  const [activeTab, setActiveTab] = useState('listings');
  const [listings, setListings] = useState([]);
  const [chats, setChats] = useState([]);
  const [loading, setLoading] = useState(true);
  
  const [searchQuery, setSearchQuery] = useState('');
  const [category, setCategory] = useState('all');
  const [city, setCity] = useState('');
  const [sortBy, setSortBy] = useState('relevant');
  const [searchZip, setSearchZip] = useState(user?.zip_code || '');
  const [radius, setRadius] = useState('25');
  const [dynamicCities, setDynamicCities] = useState([]);
  
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [newListing, setNewListing] = useState({
    title: '', description: '', category: '', price_type: 'price',
    price: '', city: user?.city || '', contact_preference: 'in_app', images: []
  });
  const [creating, setCreating] = useState(false);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [photoPreviews, setPhotoPreviews] = useState([]);
  const [uploadingPhotos, setUploadingPhotos] = useState(false);
  
  const [reportModal, setReportModal] = useState({ open: false, contentType: '', contentId: '' });
  const [reportReason, setReportReason] = useState('');
  const [reportDetails, setReportDetails] = useState('');
  const [submittingReport, setSubmittingReport] = useState(false);
  
  const [messageModal, setMessageModal] = useState({ open: false, listing: null });
  const [messageBody, setMessageBody] = useState('');
  const [sendingMessage, setSendingMessage] = useState(false);
  
  const [selectedChat, setSelectedChat] = useState(null);
  const [chatMessages, setChatMessages] = useState([]);
  const [newChatMessage, setNewChatMessage] = useState('');
  const [sendingChatMessage, setSendingChatMessage] = useState(false);

  useEffect(() => {
    if (activeTab === 'listings') {
      fetchListings();
      fetchCities();
    } else fetchChats();
  }, [activeTab, searchQuery, category, city, sortBy, searchZip, radius]);

  const fetchCities = async () => {
    try {
      const response = await api.get('/marketplace/cities');
      setDynamicCities(response.data.cities || []);
    } catch (error) {
      console.error('Error fetching cities:', error);
    }
  };

  const fetchListings = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (searchQuery) params.append('query', searchQuery);
      if (category && category !== 'all') params.append('category', category);
      if (city) params.append('city', city);
      params.append('sort_by', sortBy);
      if (searchZip) {
        params.append('zip_code', searchZip);
        params.append('radius', radius);
      }
      const response = await api.get(`/marketplace/listings?${params.toString()}`);
      setListings(response.data.listings || []);
    } catch (error) {
      console.error('Error fetching listings:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchChats = async () => {
    setLoading(true);
    try {
      const response = await api.get('/community/chats');
      setChats(response.data.chats || []);
    } catch (error) {
      console.error('Error fetching chats:', error);
    } finally {
      setLoading(false);
    }
  };

  // ===== Photo Upload =====
  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files);
    const totalPhotos = selectedFiles.length + files.length;
    if (totalPhotos > 4) {
      toast.error('Maximum 4 photos allowed');
      return;
    }
    const validFiles = files.filter(f => {
      if (!['image/jpeg', 'image/png', 'image/webp'].includes(f.type)) {
        toast.error(`${f.name}: Only JPG, PNG, WEBP allowed`);
        return false;
      }
      if (f.size > 5 * 1024 * 1024) {
        toast.error(`${f.name}: Max size is 5MB`);
        return false;
      }
      return true;
    });
    const newFiles = [...selectedFiles, ...validFiles];
    setSelectedFiles(newFiles);
    const previews = newFiles.map(f => URL.createObjectURL(f));
    setPhotoPreviews(previews);
  };

  const removePhoto = (index) => {
    URL.revokeObjectURL(photoPreviews[index]);
    setSelectedFiles(prev => prev.filter((_, i) => i !== index));
    setPhotoPreviews(prev => prev.filter((_, i) => i !== index));
  };

  const uploadPhotos = async () => {
    if (selectedFiles.length === 0) return [];
    setUploadingPhotos(true);
    const imageIds = [];
    for (const file of selectedFiles) {
      const formData = new FormData();
      formData.append('file', file);
      try {
        const res = await api.post('/upload/image', formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        imageIds.push(res.data.id);
      } catch (err) {
        toast.error(`Failed to upload ${file.name}`);
      }
    }
    setUploadingPhotos(false);
    return imageIds;
  };

  const handleCreateListing = async () => {
    if (!newListing.title || !newListing.description || !newListing.category) {
      toast.error('Please fill in all required fields');
      return;
    }
    setCreating(true);
    try {
      const imageIds = await uploadPhotos();
      const listingData = {
        ...newListing,
        price: newListing.price_type === 'price' ? parseFloat(newListing.price) || 0 : null,
        images: imageIds
      };
      await api.post('/marketplace/listings', listingData);
      toast.success('Listing created!');
      setShowCreateModal(false);
      resetCreateForm();
      fetchListings();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create listing');
    } finally {
      setCreating(false);
    }
  };

  const resetCreateForm = () => {
    photoPreviews.forEach(url => URL.revokeObjectURL(url));
    setNewListing({
      title: '', description: '', category: '', price_type: 'price',
      price: '', city: user?.city || '', contact_preference: 'in_app', images: []
    });
    setSelectedFiles([]);
    setPhotoPreviews([]);
  };

  // ===== Reporting =====
  const openReportModal = (contentType, contentId) => {
    setReportModal({ open: true, contentType, contentId });
    setReportReason('');
    setReportDetails('');
  };

  const handleSubmitReport = async () => {
    if (!reportReason) {
      toast.error('Please select a reason');
      return;
    }
    setSubmittingReport(true);
    try {
      await api.post('/moderation/report', {
        content_type: reportModal.contentType,
        content_id: reportModal.contentId,
        reason: reportReason,
        details: reportDetails || null
      });
      toast.success('Report submitted. Thank you for helping keep our community safe.');
      setReportModal({ open: false, contentType: '', contentId: '' });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit report');
    } finally {
      setSubmittingReport(false);
    }
  };

  // ===== Messaging =====
  const handleSendMessage = async () => {
    if (!messageBody.trim() || !messageModal.listing) return;
    setSendingMessage(true);
    try {
      await api.post('/messages/send', {
        recipient_id: messageModal.listing.poster_id,
        body: `Re: ${messageModal.listing.title}\n\n${messageBody}`
      });
      toast.success('Message sent!');
      setMessageModal({ open: false, listing: null });
      setMessageBody('');
    } catch (error) {
      toast.error('Failed to send message');
    } finally {
      setSendingMessage(false);
    }
  };

  const openChat = async (chatCity) => {
    setSelectedChat(chatCity);
    try {
      const response = await api.get(`/community/chats/${encodeURIComponent(chatCity)}`);
      setChatMessages(response.data.messages || []);
    } catch (error) {
      console.error('Error loading chat:', error);
    }
  };

  const sendChatMessage = async () => {
    if (!newChatMessage.trim() || !selectedChat) return;
    setSendingChatMessage(true);
    try {
      await api.post(`/community/chats/${encodeURIComponent(selectedChat)}`, {
        city: selectedChat, content: newChatMessage
      });
      setNewChatMessage('');
      const response = await api.get(`/community/chats/${encodeURIComponent(selectedChat)}`);
      setChatMessages(response.data.messages || []);
    } catch (error) {
      toast.error('Failed to send message');
    } finally {
      setSendingChatMessage(false);
    }
  };

  const getPriceDisplay = (listing) => {
    if (listing.price_type === 'free') return 'Free';
    if (listing.price_type === 'negotiable') return 'Negotiable';
    return listing.price ? `$${listing.price}` : 'Contact for price';
  };

  const getCategoryLabel = (value) => {
    return CATEGORIES.find(c => c.value === value)?.label || value;
  };

  return (
    <div className="min-h-screen bg-cream font-body">
      {/* Header */}
      <header className="bg-white border-b border-border-subtle sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/dashboard" className="font-display text-xl text-navy">
            Cali<span className="italic-teal">Moms</span> Connect
          </Link>
          <nav className="flex items-center gap-6">
            <Link to="/dashboard" className="text-sm text-text-mid hover:text-teal">Dashboard</Link>
            <Link to="/my-city" className="text-sm text-text-mid hover:text-teal">My City</Link>
            <span className="text-sm text-teal font-semibold">Community Board</span>
            <Link to="/wellness" className="text-sm text-text-mid hover:text-teal" data-testid="wellness-nav-link">Wellness</Link>
            {user?.is_admin && (
              <Link to="/admin/dashboard" className="text-xs text-text-light hover:text-teal" data-testid="admin-nav-link">Admin</Link>
            )}
          </nav>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-6 py-8">
        <div className="mb-8 flex items-center gap-4">
          <button
            onClick={() => navigate(-1)}
            className="w-10 h-10 rounded-full bg-white border border-border-subtle flex items-center justify-center text-navy hover:bg-seafoam/30 hover:text-teal transition-colors shadow-sm shrink-0"
            aria-label="Back"
            data-testid="community-board-back-btn"
          >
            <ArrowLeft size={20} />
          </button>
          <div>
            <h1 className="font-display text-3xl md:text-4xl text-navy mb-2">Community Board</h1>
            <p className="text-text-mid">Buy, sell, and connect with local moms</p>
          </div>
        </div>

        {/* Free-tier upgrade banner */}
        {!fullAccess && (
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-teal rounded-2xl p-5 mb-6 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3"
            data-testid="marketplace-upgrade-banner"
          >
            <p className="text-white text-sm leading-relaxed">
              Thrive members attend one workshop free every month, get 30% off additional workshops, and unlock the full Community Marketplace — all for $39/month.
            </p>
            <Button
              onClick={() => navigate('/pricing')}
              className="bg-white text-teal hover:bg-cream rounded-full font-semibold whitespace-nowrap"
              data-testid="banner-upgrade-btn"
            >
              Upgrade to Thrive <ArrowRight size={16} className="ml-1" />
            </Button>
          </motion.div>
        )}

        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
          <TabsList className="bg-white rounded-full p-1 border border-border-subtle">
            <TabsTrigger 
              value="listings" 
              className="rounded-full px-6 data-[state=active]:bg-teal data-[state=active]:text-white"
              data-testid="listings-tab"
            >
              Marketplace
            </TabsTrigger>
            <TabsTrigger 
              value="chats" 
              className="rounded-full px-6 data-[state=active]:bg-teal data-[state=active]:text-white"
              data-testid="chats-tab"
            >
              Community Chats
            </TabsTrigger>
          </TabsList>

          {/* ===== LISTINGS TAB ===== */}
          <TabsContent value="listings" className="space-y-6">
            <div className="bg-white rounded-2xl p-4 shadow-card space-y-3">
              <div className="flex flex-col md:flex-row gap-3">
                <div className="flex-1 relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-text-light" size={18} />
                  <Input
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    placeholder="Search listings..."
                    className="pl-10 rounded-xl border-seafoam"
                    data-testid="marketplace-search"
                  />
                </div>
                <Select value={category} onValueChange={setCategory}>
                  <SelectTrigger className="w-full md:w-44 rounded-xl border-seafoam" data-testid="category-filter">
                    <SelectValue placeholder="Category" />
                  </SelectTrigger>
                  <SelectContent>
                    {CATEGORIES.map(cat => (
                      <SelectItem key={cat.value} value={cat.value}>{cat.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Button
                  onClick={() => fullAccess ? setShowCreateModal(true) : setUpgradeModalOpen(true)}
                  className="bg-teal hover:bg-teal-deep text-white rounded-full"
                  data-testid="create-listing-btn"
                >
                  <Plus size={18} className="mr-1" /> Post
                </Button>
              </div>
              <div className="flex flex-col md:flex-row gap-3">
                <div className="relative w-full md:w-36">
                  <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 text-text-light" size={16} />
                  <Input
                    value={searchZip}
                    onChange={(e) => setSearchZip(e.target.value.replace(/\D/g, '').slice(0, 5))}
                    placeholder="Zip code"
                    className="pl-9 rounded-xl border-seafoam"
                    data-testid="zip-search"
                  />
                </div>
                <Select value={radius} onValueChange={setRadius}>
                  <SelectTrigger className="w-full md:w-32 rounded-xl border-seafoam" data-testid="radius-filter">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {RADIUS_OPTIONS.map(r => (
                      <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Select value={city || "all_cities"} onValueChange={(v) => setCity(v === "all_cities" ? "" : v)}>
                  <SelectTrigger className="w-full md:w-48 rounded-xl border-seafoam" data-testid="city-filter">
                    <SelectValue placeholder="All Cities" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all_cities">All Cities</SelectItem>
                    {dynamicCities.map(c => (
                      <SelectItem key={c} value={c}>{c}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Select value={sortBy} onValueChange={setSortBy}>
                  <SelectTrigger className="w-full md:w-36 rounded-xl border-seafoam" data-testid="sort-filter">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="relevant">Most Relevant</SelectItem>
                    <SelectItem value="nearest">Nearest</SelectItem>
                    <SelectItem value="newest">Newest</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            {loading ? (
              <div className="text-center py-12">
                <div className="animate-pulse text-teal">Loading listings...</div>
              </div>
            ) : listings.length === 0 ? (
              <div className="text-center py-12 bg-white rounded-2xl">
                <Tag className="mx-auto text-text-light mb-4" size={40} />
                <p className="text-text-mid mb-4">No listings found</p>
                <Button
                  onClick={() => fullAccess ? setShowCreateModal(true) : setUpgradeModalOpen(true)}
                  className="bg-teal hover:bg-teal-deep text-white rounded-full"
                >
                  Create the first listing
                </Button>
              </div>
            ) : (
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                {listings.map((listing, idx) => {
                  const isLocked = !fullAccess && idx >= 4;
                  const isPreview = !fullAccess;
                  return (
                    <motion.div
                      key={listing.id}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      className={`bg-white rounded-2xl overflow-hidden shadow-card transition-shadow relative ${fullAccess ? 'hover:shadow-lg' : 'cursor-pointer'}`}
                      style={isLocked ? { display: 'none' } : {}}
                      onClick={isPreview ? () => setUpgradeModalOpen(true) : undefined}
                      data-testid={`listing-${listing.id}`}
                    >
                      {/* Blur overlay for Gather users */}
                      {isPreview && (
                        <div className="absolute inset-0 z-10 flex items-center justify-center bg-white/40 backdrop-blur-[6px] rounded-2xl" data-testid={`listing-lock-${idx}`}>
                          <div className="text-center p-4">
                            <div className="w-10 h-10 bg-teal/10 rounded-full flex items-center justify-center mx-auto mb-2">
                              <Lock size={18} className="text-teal" />
                            </div>
                            <p className="text-xs text-text-mid font-medium">Upgrade to view</p>
                          </div>
                        </div>
                      )}
                      <ListingPhotoCarousel images={listing.images} />
                      
                      <div className="p-4">
                        <div className="flex items-start justify-between mb-2">
                          <h3 className="font-semibold text-navy line-clamp-1">{listing.title}</h3>
                          <span className="text-teal font-bold whitespace-nowrap ml-2">
                            {getPriceDisplay(listing)}
                          </span>
                        </div>
                        
                        <p className="text-sm text-text-mid line-clamp-2 mb-3">{listing.description}</p>
                        
                        <div className="flex flex-wrap gap-2 mb-3">
                          <Badge variant="secondary" className="bg-seafoam/30 text-navy text-xs">
                            {getCategoryLabel(listing.category)}
                          </Badge>
                          <Badge variant="outline" className="text-xs flex items-center gap-1">
                            <MapPin size={10} /> {listing.city}
                          </Badge>
                          {listing.distance_miles != null && (
                            <Badge variant="outline" className="text-xs flex items-center gap-1 text-teal border-teal/30">
                              ~{listing.distance_miles} mi
                            </Badge>
                          )}
                        </div>
                        
                        {listing.poster && fullAccess && (
                          <div className="flex items-center justify-between pt-3 border-t border-border-subtle">
                            <div className="flex items-center gap-2">
                              <Avatar className="w-6 h-6">
                                <AvatarFallback className="bg-seafoam/40 text-teal text-xs">
                                  {listing.poster.first_name?.[0]}
                                </AvatarFallback>
                              </Avatar>
                              <span className="text-xs text-text-mid">{listing.poster.first_name}</span>
                            </div>
                            <div className="flex items-center gap-1">
                              {listing.poster_id !== user?.id && (
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={(e) => { e.stopPropagation(); openReportModal('listing', listing.id); }}
                                  className="text-text-light hover:text-red-500 hover:bg-red-50 px-2"
                                  data-testid={`report-listing-${listing.id}`}
                                  title="Report listing"
                                >
                                  <Flag size={14} />
                                </Button>
                              )}
                              <Dialog open={messageModal.open && messageModal.listing?.id === listing.id} onOpenChange={(open) => !open && setMessageModal({ open: false, listing: null })}>
                                <DialogTrigger asChild>
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={(e) => { e.stopPropagation(); setMessageModal({ open: true, listing }); }}
                                    className="text-teal hover:bg-teal/10"
                                    disabled={listing.poster_id === user?.id}
                                    data-testid={`message-listing-${listing.id}`}
                                  >
                                    <MessageCircle size={14} className="mr-1" /> Message
                                  </Button>
                                </DialogTrigger>
                                <DialogContent>
                                  <DialogHeader>
                                    <DialogTitle className="font-display text-navy">
                                      Contact {listing.poster?.first_name}
                                    </DialogTitle>
                                  </DialogHeader>
                                  <p className="text-sm text-text-mid mb-4">Re: {listing.title}</p>
                                  <Textarea
                                    value={messageBody}
                                    onChange={(e) => setMessageBody(e.target.value)}
                                    placeholder="Write your message..."
                                    rows={4}
                                    className="rounded-xl border-seafoam"
                                    data-testid="listing-message-input"
                                  />
                                  <div className="flex justify-end gap-2">
                                    <Button variant="ghost" onClick={() => setMessageModal({ open: false, listing: null })}>Cancel</Button>
                                    <Button 
                                      onClick={handleSendMessage}
                                      disabled={sendingMessage || !messageBody.trim()}
                                      className="bg-teal hover:bg-teal-deep text-white rounded-full"
                                      data-testid="send-listing-message-btn"
                                    >
                                      {sendingMessage ? 'Sending...' : 'Send'}
                                    </Button>
                                  </div>
                                </DialogContent>
                              </Dialog>
                            </div>
                          </div>
                        )}
                      </div>
                    </motion.div>
                  );
                })}
              </div>
            )}
          </TabsContent>

          {/* ===== CHATS TAB ===== */}
          <TabsContent value="chats" className="space-y-6">
            <div className="grid md:grid-cols-3 gap-6">
              <div className="bg-white rounded-2xl shadow-card overflow-hidden">
                <div className="p-4 border-b border-border-subtle">
                  <h3 className="font-semibold text-navy flex items-center gap-2">
                    <Users size={18} className="text-teal" />
                    City Chats
                  </h3>
                </div>
                <div className="divide-y divide-border-subtle max-h-[500px] overflow-y-auto">
                  {chats.map(chat => (
                    <button
                      key={chat.city}
                      onClick={() => openChat(chat.city)}
                      className={`w-full p-4 text-left hover:bg-cream/50 transition-colors ${
                        selectedChat === chat.city ? 'bg-seafoam/20' : ''
                      }`}
                      data-testid={`chat-${chat.city.toLowerCase().replace(/\s+/g, '-')}`}
                    >
                      <div className="flex items-center justify-between">
                        <span className="font-medium text-navy">{chat.name}</span>
                        {chat.city === user?.city && (
                          <Badge className="bg-teal/10 text-teal text-xs">Your City</Badge>
                        )}
                      </div>
                      {chat.last_message && (
                        <p className="text-xs text-text-light line-clamp-1 mt-1">{chat.last_message}</p>
                      )}
                    </button>
                  ))}
                </div>
              </div>

              <div className="md:col-span-2 bg-white rounded-2xl shadow-card overflow-hidden flex flex-col h-[500px]">
                {selectedChat ? (
                  <>
                    <div className="p-4 border-b border-border-subtle">
                      <h3 className="font-semibold text-navy">{selectedChat} Moms</h3>
                    </div>
                    <div className="flex-1 overflow-y-auto p-4 space-y-4">
                      {chatMessages.length === 0 ? (
                        <div className="text-center text-text-light py-8">
                          No messages yet. Start the conversation!
                        </div>
                      ) : (
                        chatMessages.map(msg => (
                          <div 
                            key={msg.id} 
                            className={`flex gap-3 ${msg.sender_id === user?.id ? 'flex-row-reverse' : ''}`}
                          >
                            <Avatar className="w-8 h-8 flex-shrink-0">
                              <AvatarImage src={msg.sender?.profile_photo_url} />
                              <AvatarFallback className="bg-seafoam/40 text-teal text-xs">
                                {msg.sender?.first_name?.[0] || '?'}
                              </AvatarFallback>
                            </Avatar>
                            <div className={`max-w-[70%] ${msg.sender_id === user?.id ? 'text-right' : ''}`}>
                              <p className="text-xs text-text-light mb-1">{msg.sender?.first_name || 'Unknown'}</p>
                              <div className={`rounded-2xl px-4 py-2 ${
                                msg.sender_id === user?.id 
                                  ? 'bg-teal text-white rounded-tr-sm' 
                                  : 'bg-cream rounded-tl-sm'
                              }`}>
                                <p className="text-sm">{msg.message}</p>
                              </div>
                              {/* Report button for other users' messages */}
                              {msg.sender_id !== user?.id && (
                                <button
                                  onClick={() => openReportModal('chat_message', msg.id)}
                                  className="text-xs text-text-light hover:text-red-500 mt-1 inline-flex items-center gap-1"
                                  data-testid={`report-chat-msg-${msg.id}`}
                                >
                                  <Flag size={10} /> Report
                                </button>
                              )}
                            </div>
                          </div>
                        ))
                      )}
                    </div>
                    <div className="p-4 border-t border-border-subtle">
                      <div className="flex gap-2">
                        <Input
                          value={newChatMessage}
                          onChange={(e) => setNewChatMessage(e.target.value)}
                          placeholder="Type a message..."
                          className="rounded-full border-seafoam"
                          onKeyPress={(e) => e.key === 'Enter' && sendChatMessage()}
                          data-testid="community-chat-input"
                        />
                        <Button
                          onClick={sendChatMessage}
                          disabled={sendingChatMessage || !newChatMessage.trim()}
                          className="bg-teal hover:bg-teal-deep text-white rounded-full px-4"
                          data-testid="send-chat-message-btn"
                        >
                          <Send size={18} />
                        </Button>
                      </div>
                    </div>
                  </>
                ) : (
                  <div className="flex-1 flex items-center justify-center text-text-light">
                    Select a chat to start messaging
                  </div>
                )}
              </div>
            </div>
          </TabsContent>
        </Tabs>
      </div>

      {/* ===== CREATE LISTING MODAL ===== */}
      <Dialog open={showCreateModal} onOpenChange={(open) => { if (!open) resetCreateForm(); setShowCreateModal(open); }}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-display text-navy">Create Listing</DialogTitle>
            <DialogDescription>Share an item or service with your community</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 max-h-[60vh] overflow-y-auto pr-1">
            <div className="space-y-2">
              <Label>Title *</Label>
              <Input
                value={newListing.title}
                onChange={(e) => setNewListing({...newListing, title: e.target.value})}
                placeholder="What are you offering?"
                className="rounded-xl border-seafoam"
                data-testid="listing-title-input"
              />
            </div>
            <div className="space-y-2">
              <Label>Description *</Label>
              <Textarea
                value={newListing.description}
                onChange={(e) => setNewListing({...newListing, description: e.target.value})}
                placeholder="Describe your item or service..."
                rows={3}
                className="rounded-xl border-seafoam"
                data-testid="listing-description-input"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Category *</Label>
                <Select value={newListing.category} onValueChange={(v) => setNewListing({...newListing, category: v})}>
                  <SelectTrigger className="rounded-xl border-seafoam" data-testid="listing-category-select">
                    <SelectValue placeholder="Select" />
                  </SelectTrigger>
                  <SelectContent>
                    {CATEGORIES.filter(c => c.value !== 'all').map(cat => (
                      <SelectItem key={cat.value} value={cat.value}>{cat.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>City</Label>
                <Input
                  value={newListing.city}
                  onChange={(e) => setNewListing({...newListing, city: e.target.value})}
                  placeholder="Auto-filled from your profile"
                  className="rounded-xl border-seafoam"
                  data-testid="listing-city-input"
                />
                <p className="text-xs text-text-light">Based on your zip code. Edit if needed.</p>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Price Type</Label>
                <Select value={newListing.price_type} onValueChange={(v) => setNewListing({...newListing, price_type: v})}>
                  <SelectTrigger className="rounded-xl border-seafoam">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="price">Set Price</SelectItem>
                    <SelectItem value="free">Free</SelectItem>
                    <SelectItem value="negotiable">Negotiable</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              {newListing.price_type === 'price' && (
                <div className="space-y-2">
                  <Label>Price ($)</Label>
                  <Input
                    type="number"
                    value={newListing.price}
                    onChange={(e) => setNewListing({...newListing, price: e.target.value})}
                    placeholder="0.00"
                    className="rounded-xl border-seafoam"
                    data-testid="listing-price-input"
                  />
                </div>
              )}
            </div>
            <div className="space-y-2">
              <Label>Contact Preference</Label>
              <Select value={newListing.contact_preference} onValueChange={(v) => setNewListing({...newListing, contact_preference: v})}>
                <SelectTrigger className="rounded-xl border-seafoam">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="in_app">In-App Message</SelectItem>
                  <SelectItem value="email">Email</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            {/* Photo Upload Section */}
            <div className="space-y-2">
              <Label className="flex items-center gap-2">
                <Camera size={16} className="text-teal" /> Photos (up to 4)
              </Label>
              <div className="grid grid-cols-4 gap-2">
                {photoPreviews.map((preview, idx) => (
                  <div key={idx} className="relative aspect-square rounded-xl overflow-hidden border border-seafoam">
                    <img src={preview} alt={`Preview ${idx + 1}`} className="w-full h-full object-cover" />
                    <button
                      onClick={() => removePhoto(idx)}
                      className="absolute top-1 right-1 bg-black/60 text-white rounded-full p-0.5 hover:bg-red-500 transition-colors"
                      data-testid={`remove-photo-${idx}`}
                    >
                      <X size={14} />
                    </button>
                  </div>
                ))}
                {selectedFiles.length < 4 && (
                  <label 
                    className="aspect-square rounded-xl border-2 border-dashed border-seafoam flex flex-col items-center justify-center cursor-pointer hover:border-teal hover:bg-teal/5 transition-colors"
                    data-testid="photo-upload-trigger"
                  >
                    <Camera size={20} className="text-text-light mb-1" />
                    <span className="text-xs text-text-light">Add</span>
                    <input
                      type="file"
                      accept="image/jpeg,image/png,image/webp"
                      multiple
                      onChange={handleFileSelect}
                      className="hidden"
                      data-testid="photo-file-input"
                    />
                  </label>
                )}
              </div>
              {selectedFiles.length > 0 && (
                <p className="text-xs text-text-light">{selectedFiles.length}/4 photos selected</p>
              )}
            </div>
          </div>
          <div className="flex justify-end gap-2 pt-4">
            <Button variant="ghost" onClick={() => { resetCreateForm(); setShowCreateModal(false); }}>Cancel</Button>
            <Button 
              onClick={handleCreateListing}
              disabled={creating || uploadingPhotos}
              className="bg-teal hover:bg-teal-deep text-white rounded-full"
              data-testid="submit-listing-btn"
            >
              {uploadingPhotos ? (
                <><Loader2 size={16} className="mr-1 animate-spin" /> Uploading photos...</>
              ) : creating ? (
                'Creating...'
              ) : (
                'Create Listing'
              )}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* ===== REPORT CONTENT MODAL ===== */}
      <Dialog open={reportModal.open} onOpenChange={(open) => setReportModal({ ...reportModal, open })}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-navy">
              <AlertTriangle size={20} className="text-red-500" />
              Report Content
            </DialogTitle>
            <DialogDescription>
              Help keep our community safe. This will be reviewed by our moderation team.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Reason *</Label>
              <Select value={reportReason} onValueChange={setReportReason}>
                <SelectTrigger className="rounded-xl border-seafoam" data-testid="report-reason-select">
                  <SelectValue placeholder="Select a reason" />
                </SelectTrigger>
                <SelectContent>
                  {REPORT_REASONS.map(r => (
                    <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Additional details (optional)</Label>
              <Textarea
                value={reportDetails}
                onChange={(e) => setReportDetails(e.target.value)}
                placeholder="Provide any additional context..."
                rows={3}
                className="rounded-xl border-seafoam"
                data-testid="report-details-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setReportModal({ open: false, contentType: '', contentId: '' })}>
              Cancel
            </Button>
            <Button 
              onClick={handleSubmitReport}
              disabled={submittingReport || !reportReason}
              className="bg-red-600 hover:bg-red-700 text-white rounded-full"
              data-testid="submit-report-btn"
            >
              {submittingReport ? <><Loader2 size={16} className="mr-1 animate-spin" /> Submitting...</> : 'Submit Report'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ===== UPGRADE MODAL (Gather users) ===== */}
      <UpgradeModal open={upgradeModalOpen} onClose={() => setUpgradeModalOpen(false)} />
    </div>
  );
}
